/**
 * `useGameSession` — the client side of the dropdown login.
 *
 * The flow:
 *   1. Fetch the **slot list** over REST (`GAME_SLOTS_PATH`) before connecting, so
 *      the login dropdown shows the GM-defined slots (+ which are taken) and whether
 *      a room password is required.
 *   2. **Auto-reconnect** on mount: if a remembered token is in localStorage, open
 *      `/ws/game` and present it — the server reclaims the slot (kicking any stale
 *      socket) with no slot-pick needed.
 *   3. Otherwise the UI calls `joinSlot(slotId, password?)` or `joinAsGm(hostKey)`;
 *      on the resulting `game_welcome` we store the issued **token** in localStorage
 *      (keyed per game) and expose the authenticated **slotId** — which is the
 *      `actorId` the roster/encounter rooms key on (the slot id IS the actorId).
 *
 * This hook owns ONLY the login/identity socket (`/ws/game`); the roster/encounter
 * sockets are opened separately, stamped with the `actorId` this hook surfaces.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  WS_GAME_PATH,
  GAME_SLOTS_PATH,
  FEED_TAIL_CAP,
  decode,
  encode,
  type GameServerMessage,
  type GameRejectReason,
  type GameKickedMessage,
  type SlotListResponse,
  type Directive,
  type DirectiveBanner,
  type DirectiveTarget,
  type GameFxMessage,
  type GameSetViewMessage,
  type GameCommandMessage,
  type GameSetPasswordMessage,
  type FeedEntry,
  type GameDecisionMessage,
} from "@lancer-vtt/shared";
import {
  NARRATIVE_SCENE,
  parseTableScene,
  tableSceneEncounterId,
  type SetTableSceneCommand,
  type SetBackdropCommand,
  type TableScene,
  type DefineSlotCommand,
  type SetSlotRoleCommand,
  type RemoveSlotCommand,
  type SaveRulingCommand,
  type DeleteSavedRulingCommand,
  type SavedRuling,
  type SlotRole,
} from "@lancer-vtt/domain";
import {
  defaultEffectRegistry,
  directivesForViewState,
  runDirective,
  type EffectContext,
  type EffectRegistry,
} from "./effectRegistry";
import { storageGet, storageRemove, storageSet } from "../browserStorage";
import { clearSelectedPilot, readSelectedPilot, writeSelectedPilot } from "./selectedPilot";
import { restLogin } from "./restLogin";
import { mintSlotId } from "./slotAdmin";
import { hostKeyKey, tokenKey } from "./sessionKeys";
import { startKeepalive, reconnectDelayMs, RECONNECT_MAX_ATTEMPTS } from "../roomWs";
import { flushClientErrors, setErrorReportingSlot } from "../errorReporting/reporter";

export type GameConnStatus = "idle" | "connecting" | "authenticated" | "rejected" | "kicked" | "closed";

/** The view-state the GM has put on this slot (its standing banner). */
export type GameViewState = {
  activeBanner: DirectiveBanner | null;
}

/**
 * A **standing view-state patch** the GM sends via {@link GameSession.setView} (the durable
 * channel). Mirrors the `game_set_view` wire fields: a field left `undefined` is unchanged,
 * an explicit `null` clears it. Distinct on purpose from an ephemeral {@link Directive} — a
 * patch persists and replays, an FX does not.
 */
export type ViewStatePatch = {
  /** The standing banner to set, clear (null), or leave unchanged (undefined). */
  activeBanner?: DirectiveBanner | null;
}

/**
 * Build the **ephemeral FX** wire message — the emit core of {@link GameSession.sendFx},
 * exported so the hook and its round-trip test send the *same* shape rather than a hand-copy
 * that can drift. Distinct from {@link setViewMessage}: this is the fire-and-forget channel
 * (`game_fx` → `game_fx_event`, never persisted).
 */
export function fxMessage(directive: Directive, target: DirectiveTarget): GameFxMessage {
  return { type: "game_fx", target, directive };
}

/**
 * Build the **standing view-state** wire message — the emit core of {@link GameSession.setView},
 * exported for the same reason as {@link fxMessage}. Distinct from it: this is the durable
 * channel (`game_set_view` → `set_slot_view`, persisted + replayed on reconnect).
 */
export function setViewMessage(patch: ViewStatePatch, target: DirectiveTarget): GameSetViewMessage {
  return { type: "game_set_view", target, ...patch };
}

/**
 * Build the **`define_slot` wire message** — the emit core of {@link GameSession.defineSlot},
 * exported for the same reason as {@link fxMessage}/{@link setViewMessage}. Rides the generic
 * `game_command` envelope.
 */
export function defineSlotMessage(args: { actorId: string; slotId: string; name: string; role: SlotRole }): GameCommandMessage {
  const { actorId, slotId, name, role } = args;
  const command: DefineSlotCommand = { type: "define_slot", actorId, slotId, name, role };
  return { type: "game_command", actorId, command };
}

/**
 * Build the **`set_slot_role` wire message** — the emit core of {@link GameSession.setSlotRole},
 * exported for the same reason as {@link defineSlotMessage}/{@link removeSlotMessage}. Rides the
 * generic `game_command` envelope, exactly like `define_slot`/`remove_slot`.
 */
export function setSlotRoleMessage(args: { actorId: string; slotId: string; role: SlotRole }): GameCommandMessage {
  const { actorId, slotId, role } = args;
  const command: SetSlotRoleCommand = { type: "set_slot_role", actorId, slotId, role };
  return { type: "game_command", actorId, command };
}

/**
 * Build the **`remove_slot` wire message** — the emit core of {@link GameSession.removeSlot},
 * exported for the same reason as {@link defineSlotMessage}. Rides the generic `game_command`
 * envelope, exactly like `define_slot`.
 */
export function removeSlotMessage(args: { actorId: string; slotId: string }): GameCommandMessage {
  const { actorId, slotId } = args;
  const command: RemoveSlotCommand = { type: "remove_slot", actorId, slotId };
  return { type: "game_command", actorId, command };
}

/**
 * Build the **`save_ruling` wire message** — the emit core of {@link GameSession.saveRuling},
 * exported for the same reason as {@link defineSlotMessage}. Rides the generic `game_command`
 * envelope, exactly like `define_slot`.
 *
 * A save onto an id that already exists **overwrites** it — that is the edit gesture, so the
 * rename control on the EDIT-surface library manager sends this too, not a distinct command.
 */
export function saveRulingMessage(args: { actorId: string; savedRuling: SavedRuling }): GameCommandMessage {
  const { actorId, savedRuling } = args;
  const command: SaveRulingCommand = { type: "save_ruling", actorId, savedRuling };
  return { type: "game_command", actorId, command };
}

/**
 * Build the **`delete_saved_ruling` wire message** — the emit core of
 * {@link GameSession.deleteSavedRuling}. Same extraction rationale as {@link saveRulingMessage}.
 */
export function deleteSavedRulingMessage(args: { actorId: string; id: string }): GameCommandMessage {
  const { actorId, id } = args;
  const command: DeleteSavedRulingCommand = { type: "delete_saved_ruling", actorId, id };
  return { type: "game_command", actorId, command };
}

/**
 * Build the **`game_set_password` wire message** — the emit core of
 * {@link GameSession.setRoomPassword}, exported for the same reason as
 * {@link defineSlotMessage}/{@link removeSlotMessage}. **Deliberately NOT a `game_command`** —
 * see the note above `GameSetPasswordMessage` in `shared/src/index.ts`: the server must hash
 * the plaintext (argon2id) before it becomes a `set_room_password` command, work the pure
 * reducer can't do, so this rides its own dedicated message type. The plaintext crosses the
 * wire here and is hashed **server-side only** — never hash client-side. `null` clears the
 * password (open join).
 */
export function setPasswordMessage(password: string | null): GameSetPasswordMessage {
  return { type: "game_set_password", password };
}

/**
 * One slot's **live presence** — derived off the Game room's `game_change` broadcast (the
 * durable `state.slots` joined with the live `connected` set), so it reflects who is logged
 * into which slot in real time. `connected` flips on join/leave/reclaim.
 */
export type SlotPresence = {
  id: string;
  name: string;
  role: "gm" | "player";
  /** True while a live socket holds this slot (someone is logged in to it). */
  connected: boolean;
}

/** The loose slot shape the Game room puts on the wire inside the opaque `GameState`. */
type WireSlot = { id: string; name: string; role: string };

/** `decisions` minus `promptId`, or the same object when it isn't there. */
function withoutDecision(prev: Record<string, GameDecisionMessage>, promptId: string): Record<string, GameDecisionMessage> {
  if (!(promptId in prev)) return prev;
  const next = { ...prev };
  delete next[promptId];
  return next;
}

/**
 * The pending-decision map after one `/ws/game` message. A `game_decision` pins its card, a
 * `game_decision_resolved` clears it, and a `game_welcome` clears them all: the server re-sends
 * every card still open right after the welcome, so what survives a reconnect is exactly what the
 * server still has, and a card whose decision died with a restarted server goes (`stuck-brace`).
 */
export function nextDecisions(
  prev: Record<string, GameDecisionMessage>,
  msg: Extract<GameServerMessage, { type: "game_welcome" | "game_decision" | "game_decision_resolved" }>,
): Record<string, GameDecisionMessage> {
  if (msg.type === "game_welcome") return Object.keys(prev).length === 0 ? prev : {};
  if (msg.type === "game_decision") return { ...prev, [msg.promptId]: msg };
  return withoutDecision(prev, msg.promptId);
}

/**
 * Read the **table scene** off a Game-room state blob (`game_welcome.state` /
 * `game_change.state`, opaque `unknown` on the wire): narrative, or the encounter on the
 * table (the live fight players discover and join). Narrative when absent or malformed.
 */
export function tableSceneFromState(state: unknown): TableScene {
  return parseTableScene((state as { tableScene?: unknown } | null)?.tableScene) ?? NARRATIVE_SCENE;
}

/** Read the **narrative backdrop** (a codex entry id) off a Game-room state blob; null when none. */
export function backdropFromState(state: unknown): string | null {
  const backdrop = (state as { backdrop?: unknown } | null)?.backdrop;
  return typeof backdrop === "string" ? backdrop : null;
}

/**
 * Read whether a **room password is set** off a Game-room state blob (`game_welcome.state`
 * / `game_change.state`, opaque `unknown` on the wire) — the LIVE counterpart of the
 * pre-connect REST `SlotListResponse.passwordRequired`.
 *
 * **Why this exists instead of reusing `passwordRequired`:** that REST field is a
 * one-shot fetch (`GET /api/game/slots`, driven by `GameLogin.tsx`'s dropdown) taken
 * *before* a socket connects — it never updates again for a client already connected.
 * `PublicGameState.passwordSet` (`packages/domain/src/game/project.ts`) rides every
 * `game_welcome`/`game_change` broadcast, so reading the boolean here off that live state
 * is the same move `slotsFromState`/`tableSceneFromState` make for slots/the table
 * scene. **The wire never carries the password hash at all** — the server projects
 * `GameState` down to `PublicGameState` before it ever reaches a socket, so there is
 * nothing here to derive a boolean from except the boolean itself.
 */
export function passwordSetFromState(state: unknown): boolean {
  return (state as { passwordSet?: boolean } | null)?.passwordSet ?? false;
}

/**
 * Read the GM's **saved-ruling library** off a Game-room state blob (`game_welcome.state` /
 * `game_change.state`, opaque `unknown` on the wire). Sorted by label so the palette's own
 * rows are stable across re-renders, the same reason {@link slotsFromState} sorts by id.
 *
 * **Every client receives this, GM and player alike** — `projectGame` is role-blind by
 * design (see `PublicGameState`'s docstring). A player client simply has no UI that reads
 * it; the palette that does is already `isGm`-gated at its host. That is wire-visibility,
 * not a leak.
 */
export function savedRulingsFromState(state: unknown): SavedRuling[] {
  const saved = (state as { savedRulings?: Record<string, SavedRuling> } | null)?.savedRulings;
  if (saved === undefined) return [];
  return Object.values(saved).sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Derive the live slot-presence list from a Game-room state blob (`game_welcome.state`
 * or `game_change.state`, opaque `unknown` on the wire) and the live `connected` slot
 * ids. Sorted by id so the lobby list is stable across re-renders.
 */
export function slotsFromState(state: unknown, connected: string[]): SlotPresence[] {
  const slots = (state as { slots?: Record<string, WireSlot> } | null)?.slots;
  if (slots === undefined) return [];
  const live = new Set(connected);
  return Object.values(slots)
    .map((s) => ({ id: s.id, name: s.name, role: s.role as "gm" | "player", connected: live.has(s.id) }))
    .sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * The banner on the players' screens, read off a Game-room state blob: the first player slot's
 * (by id) standing `activeBanner`. The Director sends its transmission to every player slot at
 * once, so they share one; `null` when no player slot carries one.
 */
export function playerBannerFromState(state: unknown): DirectiveBanner | null {
  const slots = (state as { slots?: Record<string, WireSlot & { viewState?: { activeBanner?: DirectiveBanner | null } }> } | null)?.slots;
  if (slots === undefined) return null;
  const players = Object.values(slots)
    .filter((s) => s.role === "player")
    .sort((a, b) => a.id.localeCompare(b.id));
  return players.map((s) => s.viewState?.activeBanner ?? null).find((b) => b !== null) ?? null;
}

export type GameSession = {
  status: GameConnStatus;
  /**
   * True while the mount-time **token auto-reclaim** is still resolving — from the
   * first render until either the reclaim connects (→ `authenticated`) or we know
   * there's nothing to reclaim (no remembered token, or the reclaim was rejected).
   *
   * The global login gate reads this to show a "reconnecting" state instead of the
   * login form during the reclaim window: without it, a page reload flickers the
   * login form for the beat between mount and the reclaim socket opening, because
   * `status` starts at `idle` (indistinguishable from "logged out") while the slot
   * list is still being fetched and the token hasn't been presented yet. Once this
   * is `false` and `status` isn't `authenticated`, the visitor is genuinely logged
   * out and the login form is correct to show.
   */
  reclaiming: boolean;
  /**
   * True while a session that was welcomed in this page lifetime is reclaiming after a
   * **transport** close — a wifi blip, a sleeping laptop, a server restart. Distinct from
   * logged-out: `actorId`, `token` and `role` are unchanged throughout, so `RequireAuth` keeps
   * the surface mounted and the app-wide link pill says why nothing syncs. False once the
   * session is welcomed again, and never true after an auth loss (reject, kick, logout), which
   * leaves the surface through the login path instead.
   */
  linkDown: boolean;
  /**
   * The drop has outlasted the automatic fast-retry budget (`RECONNECT_MAX_ATTEMPTS`, ~45s).
   * An escalation signal, not a give-up: the session keeps retrying at the 15s cap. Implies
   * {@link linkDown}. Mirrors `useRoomController`'s `linkLost`.
   */
  linkStale: boolean;
  /**
   * Reconnect NOW: drop the pending backoff, grant a fresh retry budget and re-present the
   * token immediately. A no-op while the socket is open or after an auth loss. Mirrors
   * `useRoomController.reconnect`.
   */
  reconnect: () => void;
  /** The pre-connect slot list (null until fetched). */
  slotList: SlotListResponse | null;
  /** The authenticated slot id — the actorId for every other room (null until authenticated). */
  actorId: string | null;
  /**
   * The pilot the player has picked to drive the desktop. Purely a *view* concept —
   * never a server "active pilot". `null` until the player picks one (or if nothing was
   * remembered for this slot). Persisted in localStorage keyed on the slot id, restored
   * on (re)authentication.
   */
  selectedPilotId: string | null;
  /**
   * The reclaim token issued/echoed in `game_welcome` (null until authenticated). The
   * roster/encounter sockets present this in their hello handshake so the server binds
   * them to the same verified slot.
   */
  token: string | null;
  role: "gm" | "player" | null;
  /**
   * The GM host key this session authenticated with, if any (null for a player, or a GM
   * reconnected by token before re-entering it). Held so GM-gated REST calls (e.g. the
   * scene-preset save, which needs the {@link HOST_KEY_HEADER}) can authenticate without a
   * re-prompt. Persisted to localStorage like the reclaim token — same hand-editable
   * world-folder credential, never a secret beyond what the GM already typed.
   */
  hostKey: string | null;
  /** The slot's standing view-state, re-sent on (re)join. */
  viewState: GameViewState | null;
  /**
   * The live **slot presence** — every defined slot with name + role + whether a live
   * connection holds it — pushed off the Game room's `game_change` broadcast. Empty until
   * the first welcome/change arrives.
   */
  slots: SlotPresence[];
  /** The banner standing on the players' screens ({@link playerBannerFromState}), or null. */
  playerBanner: DirectiveBanner | null;
  /**
   * The **table scene** the GM is showing (`decisions/0203` §2): narrative, or an encounter.
   * Read off the durable `GameState` in every `game_welcome`/`game_change` broadcast.
   */
  tableScene: TableScene;
  /**
   * The encounter on the table, or `null` when the scene is narrative — derived from
   * {@link tableScene}. The live fight: COMBAT gates its END COMBAT on it.
   */
  activeEncounterId: string | null;
  /** The codex entry the GM pinned as the narrative backdrop, or null — live off every broadcast. */
  backdrop: string | null;
  /**
   * Whether the room currently has a **password set** — read LIVE from
   * `PublicGameState.passwordSet` on every `game_welcome`/`game_change` broadcast via
   * {@link passwordSetFromState}, mirroring how {@link slots} is derived off the same
   * broadcasts. **The wire never carries the hash at all** — the server projects it out
   * before broadcast. Distinct from `slotList?.passwordRequired`, which is a one-shot
   * pre-connect REST read that never updates for an already-connected client.
   */
  passwordSet: boolean;
  /**
   * The GM's **saved-ruling library** — read LIVE off every `game_welcome`/`game_change`
   * broadcast via {@link savedRulingsFromState}, exactly like {@link slots}. Sorted by
   * label. Empty on a fresh world, which is correct: the palette's seven presets are
   * code-shipped, so it works with zero setup.
   */
  savedRulings: SavedRuling[];
  /** Why the last hello was rejected, for surfacing in the login UI. */
  rejectReason: GameRejectReason | null;
  /**
   * Why the session was **kicked** (`game_kicked.reason`), for surfacing on the `/login`
   * card `RequireAuth` redirects a kicked client to. Distinct from {@link rejectReason} —
   * a kick evicts an *established* connection, a reject fails an *incoming* hello, and the
   * two must not be conflated (the login card needs to know which actually happened).
   * `"removed"` — the GM removed this slot; the remembered token was cleared (the slot and
   * its server-side token are both gone, so there's nothing left to reclaim).
   * `"evicted"` — another connection claimed this slot (a second tab/device); the token is
   * kept, since the slot itself still exists and this device may reclaim it later.
   * Cleared at the start of the next connect/join attempt (`connect`/`loginThenConnect`) and
   * on an explicit {@link forget}, so a fresh login never shows a stale kick line.
   */
  kickReason: GameKickedMessage["reason"] | null;
  /**
   * The game-scoped **chat & activity feed** — the bounded tail (oldest→newest) of
   * interleaved chat / system / roll entries. Seeded from `game_welcome.feed` on (re)join
   * and appended on every `chat_appended` broadcast. Empty until the first welcome.
   * COMMS.app reads this; display names resolve off {@link slots}.
   */
  feed: FeedEntry[];
  /**
   * The **feed freshness baseline** — the max `ts` across the most recent
   * `game_welcome.feed` backfill (`0` for an empty backfill), re-seeded on **every**
   * `game_welcome` (including a mid-session reconnect that re-delivers the tail). It exists
   * so a consumer can tell a **backfilled** entry (already-known history,
   * `ts <= feedBaselineTs`) from a **genuinely new live append** (`ts > feedBaselineTs`) —
   * `game_welcome.feed` and `chat_appended` are otherwise indistinguishable once folded into
   * the same `feed` array. Deliberately generic rather than dice-specific, though
   * {@link DiceFeedSubscriber} (don't replay history as a dice-throw flurry on reload) is the
   * consumer that needs it. `0` until the first welcome.
   */
  feedBaselineTs: number;
  /**
   * The **live pending decisions** — an ephemeral, `promptId`-keyed map fed by `game_decision`
   * (added) / `game_decision_resolved` (removed). NEVER a `FeedEntry`, NEVER persisted. Reset on
   * every `game_welcome` ({@link nextDecisions}): the server re-sends each card still open for this
   * slot right behind the welcome, so a card resolved during an outage, or one whose decision died
   * with a restarted server, does not stay pinned. COMMS.app's
   * `DecisionCard` reads this to render the sticky, resolvable card for Brace/Overwatch/an
   * authored offer; it unpins the moment the matching `game_decision_resolved` arrives (or on
   * `forget()`, since a logged-out client holds no live decisions).
   */
  decisions: Record<string, GameDecisionMessage>;
  /**
   * Drop one decision card locally, without the server's `game_decision_resolved` — for a card
   * whose decision this client can no longer answer (its prompt was lost across a reconnect or a
   * server restart), so a card is never un-dismissable (`stuck-brace`).
   */
  dismissDecision: (promptId: string) => void;
  /**
   * Post a chat line into the game feed. Sends `chat_post { text }` on the `/ws/game`
   * socket — the author is server-stamped (this slot), never asserted here. No-op if the
   * socket isn't open/authenticated, or the text is blank.
   *
   * **Every session writer returns whether it sent** (`defineSlot` returns its minted id or
   * `null`): `false` means the link is down (or the write was a local no-op), and the caller keeps
   * its input and shows the link-down note rather than clearing as if it had gone out.
   */
  postChat: (text: string) => boolean;
  /**
   * Pick which owned pilot drives the desktop. Sets the in-session selection and persists
   * it for this slot. No-op if not authenticated (there's no slot id to key the persistence
   * on).
   */
  selectPilot: (pilotId: string) => void;
  /**
   * Clear the desktop pilot pick. Resets the in-session selection and drops the persisted
   * pick for this slot, so the home router falls back to the {@link PilotPicker}. Mirrors
   * {@link selectPilot}; no-op if not authenticated (no slot id to key the persistence on).
   */
  deselectPilot: () => void;
  /** Claim a named player slot (+ optional room password). */
  joinSlot: (slotId: string, password?: string) => void;
  /** Authenticate as the GM operator with the host/admin key. */
  joinAsGm: (hostKey: string) => void;
  /**
   * **GM-only**: set the table scene — an encounter (the live fight players discover and join) or
   * narrative. Rides the existing `/ws/game` socket as a `game_command { set_table_scene }`.
   * Identity is server-bound — the room re-attributes to the authenticated slot and gates the
   * channel GM-only — so a non-GM caller is a server-side no-op. No-op locally too if the socket
   * isn't open/authed.
   */
  setTableScene: (scene: TableScene) => boolean;
  /** **GM-only**: pin a codex entry as the narrative backdrop, or clear it (`null`). Undoable. */
  setBackdrop: (entryId: string | null) => boolean;
  /**
   * **GM-only**: define (create) a new slot, riding the same `/ws/game` `game_command` path as
   * {@link setTableScene}. The `slotId` is **caller-minted here** (never by the reducer)
   * by slugifying `name` via `mintSlotId` against the live {@link slots} list, so a collision
   * with an already-defined id is avoided *before* sending — the server treats a `define_slot`
   * collision as a silent no-op (no error reply), so there is no round-trip to react to.
   * `role` defaults to `"player"`. Returns the minted slot id, or `null` if the send was a
   * local no-op (unauthenticated socket). Identity is server-bound exactly like
   * `setTableScene` — no local role pre-check, the server gate is the wall; a non-GM
   * caller is a server-side no-op.
   */
  defineSlot: (name: string, role?: SlotRole) => string | null;
  /**
   * **GM-only**: change an existing slot's role, gm ⇄ player — the same `/ws/game`
   * `game_command` path as {@link defineSlot}/{@link removeSlot}/{@link setTableScene}.
   * No local role pre-check — the server gate is the wall; a non-GM caller is a server-side
   * no-op. The reducer itself refuses to demote the room's last `gm`-role slot to `"player"`
   * (`packages/domain/src/game/reducer.ts`, mirrors `remove_slot`'s guard); that rejection
   * is, like every `game_command` reject, a **silent no-op on the wire** (no `game_reject`,
   * no broadcast — see the file-header note on `applyWireCommand`), so this method sends
   * unconditionally and the UI (`GmSlots.tsx`) disables the doomed demote affordance for
   * that one slot up front, via `isOnlyGmSlot`, rather than reacting to a reply that will
   * never come. Promoting a player to `gm`, or a `gm`→`gm` no-op relabel, are both always
   * legal and never disabled.
   */
  setSlotRole: (slotId: string, role: SlotRole) => boolean;
  /**
   * **GM-only**: remove an existing slot — the same `/ws/game` `game_command` path as
   * {@link defineSlot}/{@link setTableScene}. No local role pre-check — the server
   * gate is the wall; a non-GM caller is a server-side no-op. The reducer itself refuses
   * to remove the room's last `gm`-role slot (`packages/domain/src/game/reducer.ts`); that
   * rejection is, like every `game_command` reject, a **silent no-op on the wire** (no
   * `game_reject`, no broadcast — see the file-header note on `applyWireCommand`), so this
   * method sends unconditionally and the UI (`GmSlots.tsx`) disables the affordance for
   * that one slot up front rather than reacting to a reply that will never come.
   *
   * **Removing a `taken` slot does not evict its live connection.** `GameRoom` never closes
   * or re-binds a socket when the slot it's bound to is removed — the connection just keeps
   * running, orphaned, until it disconnects on its own; only then does its next reconnect
   * attempt fail (the reclaim token now resolves to no slot → `bad_token`). There is no
   * server-side kick to react to, so the UI can't "wait and see" — see `GmSlots.tsx` for how
   * it surfaces this to the GM instead (a confirmation before removing a taken slot).
   */
  removeSlot: (slotId: string) => boolean;
  /**
   * **GM-only**: set (a string) or clear (`null`) the **room password** — a DIFFERENT wire
   * path from {@link defineSlot}/{@link removeSlot}/{@link setTableScene}: it sends
   * `game_set_password` directly, never wrapped in a `game_command` envelope, because the
   * server must hash the plaintext (argon2id, `backend/src/passwords.ts`) before it becomes
   * the `set_room_password` command — work the pure reducer can't do. **The plaintext
   * crosses the wire and is hashed server-side; this never hashes client-side.** No local
   * role pre-check — the server gate (`isGm`) is the wall, exactly like the other three
   * commands; a non-GM caller is a server-side no-op. No-op locally too if the socket isn't
   * open/authenticated.
   */
  setRoomPassword: (password: string | null) => boolean;
  /**
   * **GM-only**: mint or overwrite an entry in the {@link savedRulings} library. Passing a
   * `savedRuling` whose `id` already exists **is the edit** — there is deliberately no
   * separate edit method, so "keep the one I just built" (the PLAY-arm save affordance) and
   * "rename the one I saved" (the EDIT-surface manager) are the same call with the same
   * inverse.
   *
   * No local role pre-check — the server gate (`isGm`) is the wall, exactly like
   * {@link defineSlot}. No-op locally if the socket isn't open/authenticated.
   */
  saveRuling: (savedRuling: SavedRuling) => boolean;
  /** **GM-only**: delete one saved ruling. Undoable; never touches an already-granted status. */
  deleteSavedRuling: (id: string) => boolean;
  /**
   * **GM-only**: fire an **ephemeral FX** (`game_fx`) at the targeted clients — the fire-and-forget
   * half of the directive split. The server routes it to live sockets as a `game_fx_event`; it is
   * **never persisted, never replayed** (a client reconnecting after one fired never sees it).
   * `target` defaults to `"all"`. A new effect is a one-line `register(...)` in
   * `effectRegistry.ts` — **never** a new wire message; the `Directive` envelope is generic.
   *
   * GM-gated at the UI (no-op unless `role === "gm"`); the real wall is the server gate
   * (`GameRoom.handle` rejects a non-`gm` slot's directive). No-op if the socket isn't open/authed.
   * Kept **distinct** from {@link setView} so a caller can't accidentally send an FX as a standing
   * view (the ephemeral channel must not be confused with the durable one).
   */
  sendFx: (directive: Directive, target?: DirectiveTarget) => boolean;
  /**
   * **GM-only**: set a **standing view-state** (`game_set_view`) on the targeted slots — the durable
   * half of the directive split. The server patches each slot's `viewState` via
   * `set_slot_view`, **persists it to `session.json`, and re-sends it on reconnect** (via
   * `game_welcome.viewState`), so a dropped/late player still sees the banner. `target`
   * defaults to `"all"`. A patch field left `undefined` is unchanged; an explicit `null` clears it.
   *
   * The locally-held {@link GameSession.viewState} updates from the server's own `game_view` push
   * (server-authoritative) — **not** set optimistically here. GM-gated at the UI; the server gate is
   * the real wall. No-op if the socket isn't open/authed. Kept **distinct** from {@link sendFx} so a
   * caller can't accidentally send a standing view as an FX (the durable channel must persist).
   */
  setView: (patch: ViewStatePatch, target?: DirectiveTarget) => boolean;
  /** Forget the remembered token + drop the session (log out). */
  forget: () => void;
}

function gameWsUrl(): string {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${location.host}${WS_GAME_PATH}`;
}

/**
 * Optional GM-directive wiring. Pass an {@link EffectContext} (the client's imperative
 * capabilities: set the banner, fire a transient FX) and the hook runs the
 * **client effect registry** as directives arrive:
 *  - **standing** view-state (re-derived from `game_welcome.viewState` on every
 *    reconnect and from a live `game_view` push), so a reconnecting client gets its banner back;
 *  - **ephemeral** FX from `game_fx_event`, run exactly once.
 * Omit it and directives are simply not applied (the login flow still works).
 */
export type UseGameSessionOpts = {
  effects?: EffectContext;
  registry?: EffectRegistry;
}

export function useGameSession(opts: UseGameSessionOpts = {}): GameSession {
  const [status, setStatus] = useState<GameConnStatus>("idle");
  /**
   * Mirrors {@link status} for the focus/visibility refetch effect below, which needs the
   * LATEST status inside a long-lived DOM listener without resubscribing on every status
   * change (the same ref-mirror idiom `tokenRef`/`connectRef` already use in this hook).
   */
  const statusRef = useRef<GameConnStatus>("idle");
  statusRef.current = status;
  // Starts true: until the mount effect has fetched the slot list and decided whether
  // a remembered token exists, we can't tell "logged out" from "about to reclaim", so
  // the gate must hold the reconnecting state rather than flash the login. It resolves
  // to false the moment we know there's nothing to reclaim, or once a reclaim attempt
  // settles (welcome → authenticated, or reject).
  const [reclaiming, setReclaiming] = useState(true);
  const [linkDown, setLinkDown] = useState(false);
  const [linkStale, setLinkStale] = useState(false);
  const [slotList, setSlotList] = useState<SlotListResponse | null>(null);
  const [actorId, setActorId] = useState<string | null>(null);
  const [selectedPilotId, setSelectedPilotId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [role, setRole] = useState<"gm" | "player" | null>(null);
  const [hostKey, setHostKey] = useState<string | null>(null);
  const [viewState, setViewState] = useState<GameViewState | null>(null);
  const [slots, setSlots] = useState<SlotPresence[]>([]);
  const [playerBanner, setPlayerBanner] = useState<DirectiveBanner | null>(null);
  const [tableScene, setTableSceneState] = useState<TableScene>(NARRATIVE_SCENE);
  const activeEncounterId = tableSceneEncounterId(tableScene);
  const [backdrop, setBackdropState] = useState<string | null>(null);
  const [passwordSet, setPasswordSet] = useState<boolean>(false);
  const [savedRulings, setSavedRulings] = useState<SavedRuling[]>([]);
  const [rejectReason, setRejectReason] = useState<GameRejectReason | null>(null);
  const [kickReason, setKickReason] = useState<GameKickedMessage["reason"] | null>(null);
  const [feed, setFeed] = useState<FeedEntry[]>([]);
  const [feedBaselineTs, setFeedBaselineTs] = useState<number>(0);
  const [decisions, setDecisions] = useState<Record<string, GameDecisionMessage>>({});
  const wsRef = useRef<WebSocket | null>(null);
  const gameNameRef = useRef<string>("");

  // ── Reconnect machinery ───────────────────────────────────────────────────────
  // Losing the socket is NORMAL — an idle path gets evicted by NAT, a laptop sleeps, a
  // deploy restarts the server — so a drop is never terminal: the session recovers by
  // re-presenting the remembered token rather than bouncing the player to `/login`.
  /**
   * Connection generation. Bumped on every {@link connect}; each socket's handlers
   * capture their own and no-op once superseded. Without this a *previous* socket's
   * late `onclose` would run `setStatus("closed")` over a freshly-authenticated new
   * socket — a live race, since reconnect calls `connect` repeatedly.
   */
  const genRef = useRef(0);
  /** The most recent token worth re-presenting; `null` means there's nothing to reclaim. */
  const tokenRef = useRef<string | null>(null);
  /** Reconnect attempts since the last successful welcome (drives the backoff). */
  const attemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopKeepaliveRef = useRef<(() => void) | null>(null);
  /**
   * Set when the session must NOT come back on its own: an explicit logout, an unmount,
   * a rejected credential, or a `game_kicked`. The kick case is the load-bearing one —
   * eviction means another socket deliberately took this slot, so auto-reconnecting would
   * evict *it* right back and two tabs would kick each other forever.
   */
  const abandonedRef = useRef(false);
  /**
   * True once this page has been welcomed and until an auth loss or logout — what separates a
   * transport drop of a live session ({@link GameSession.linkDown}) from a first-load reclaim,
   * which keeps `RequireAuth`'s full-screen relink card.
   */
  const welcomedRef = useRef(false);
  /** Lets the close handler re-enter `connect` without a circular `useCallback`. */
  const connectRef = useRef<((token: string) => void) | null>(null);

  // Keep the effect wiring in refs so the long-lived `connect` closure always reads
  // the latest context/registry without re-opening the socket.
  const effectsRef = useRef<EffectContext | undefined>(opts.effects);
  effectsRef.current = opts.effects;
  const registryRef = useRef<EffectRegistry>(opts.registry ?? defaultEffectRegistry());
  if (opts.registry !== undefined) registryRef.current = opts.registry;

  /** Apply a slot's standing view-state (its banner) through the registry. */
  const applyStanding = useCallback((view: GameViewState): void => {
    const ctx = effectsRef.current;
    if (ctx === undefined) return;
    for (const directive of directivesForViewState(view)) {
      runDirective({ registry: registryRef.current, directive, ctx, expectedKind: "standing" });
    }
  }, []);

  /**
   * Retire the live socket so it can no longer drive state: cancel its keepalive, strip
   * its handlers, and close it. Paired with the {@link genRef} check inside the handlers —
   * belt and braces, because a `close()` issued here still delivers its event later.
   */
  const detachSocket = useCallback((): void => {
    const ws = wsRef.current;
    wsRef.current = null;
    stopKeepaliveRef.current?.();
    stopKeepaliveRef.current = null;
    if (ws === null) return;
    ws.onopen = null;
    ws.onclose = null;
    ws.onerror = null;
    ws.onmessage = null;
    ws.close();
  }, []);

  /**
   * Queue a token re-presentation after an unexpected close, paced by
   * {@link reconnectDelayMs}. Holds `reclaiming` true across the whole retry window so
   * `RequireAuth` never redirects: a session welcomed earlier in this page is `linkDown`, and
   * `RequireAuth` keeps its children mounted under the app-wide link pill; a first-load reclaim
   * holds on the "Relinking to the omninet…" card.
   *
   * **Retries indefinitely — never give up.** Falling through to `/login` would unmount
   * the stage's COMBAT.app, destroying Pixi's renderer, its WebGL context and its shaders; a transport
   * drop must not cost the player the board.
   *
   * **This does NOT strand a visitor on a spinner**: a genuine auth failure never reaches here.
   * `game_reject`/`bad_token` sets `abandonedRef` and clears the token (see the message
   * handler), and both are early-returned above — so the only thing that retries forever is a
   * **transport** failure, which is precisely the case that should.
   *
   * The `online`/`visibilitychange` listener below short-circuits the backoff on a wake.
   */
  const scheduleReconnect = useCallback((): void => {
    if (abandonedRef.current) return;
    const token = tokenRef.current;
    if (token === null) {
      setReclaiming(false); // nothing to reclaim — this is a genuine logged-out state
      setLinkDown(false);
      return;
    }
    if (welcomedRef.current) setLinkDown(true);
    // Escalate once the fast budget is spent — still retrying, see `linkStale`.
    if (attemptRef.current >= RECONNECT_MAX_ATTEMPTS) setLinkStale(true);
    // The exponent is clamped because past the cap every further attempt is the same 15s
    // anyway, and an overnight tab would otherwise drift `2 ** n`.
    const delay = reconnectDelayMs(Math.min(attemptRef.current, RECONNECT_MAX_ATTEMPTS));
    attemptRef.current += 1;
    setReclaiming(true);
    if (retryTimerRef.current !== null) clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      connectRef.current?.(token);
    }, delay);
  }, []);

  /**
   * Open the login socket with an already-minted reclaim **token** and bind on welcome.
   * The ws hello is token-only — credential auth runs over REST first (see
   * {@link loginThenConnect}); the reclaim path already holds a token.
   *
   * Also the reconnect entry point: {@link scheduleReconnect} re-enters here with the
   * remembered token, so everything below must be safe to run repeatedly.
   */
  const connect = useCallback((token: string) => {
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    // A fresh connect is intent to be online — clear an abandon left by a prior logout
    // or rejection so the socket that follows can retry on its own.
    abandonedRef.current = false;
    tokenRef.current = token;
    const gen = ++genRef.current; // supersede every earlier socket's handlers
    detachSocket();
    setRejectReason(null);
    // A fresh connect/reclaim attempt supersedes any prior kick — don't let a stale kick
    // line hang around once the session is trying again.
    setKickReason(null);
    setStatus("connecting");
    const ws = new WebSocket(gameWsUrl());
    wsRef.current = ws;
    const onDrop = (): void => {
      if (gen !== genRef.current) return; // a superseded socket's late close must not touch live state
      stopKeepaliveRef.current?.();
      stopKeepaliveRef.current = null;
      setStatus((s) => (s === "kicked" || s === "rejected" ? s : "closed"));
      // Losing the socket is recoverable, not terminal: re-present the token rather than
      // dropping the player at /login with a perfectly good session in localStorage.
      scheduleReconnect();
    };
    ws.onopen = () => {
      if (gen !== genRef.current) return;
      ws.send(encode({ type: "game_hello", token }));
      // Keep the path warm from the moment it opens: an idle socket gets silently evicted
      // mid-path (measured: 60s on the live deployment) — see `startKeepalive`'s note.
      stopKeepaliveRef.current?.();
      // A dead path runs the drop path now rather than when TCP finally delivers the close; the
      // handler is detached first so that late close cannot run it a second time.
      stopKeepaliveRef.current = startKeepalive(ws, {
        onDead: () => {
          ws.onclose = null;
          onDrop();
        },
      });
    };
    ws.onclose = onDrop;
    // A connection-level error always precedes a close — let `onclose` own the decision so
    // the retry policy lives in exactly one place.
    ws.onerror = () => {};
    ws.onmessage = (evt) => {
      if (gen !== genRef.current) return;
      const msg = decode<GameServerMessage>(String(evt.data));
      switch (msg.type) {
        case "game_welcome": {
          // A live session again: reset the backoff so the NEXT drop retries promptly,
          // and hold the (possibly re-issued) token as the thing to re-present.
          attemptRef.current = 0;
          tokenRef.current = msg.token;
          welcomedRef.current = true;
          setLinkDown(false);
          setLinkStale(false);
          setActorId(msg.slotId);
          // The session is live again — stamp crash reports with this slot, and push out
          // anything that piled up while the socket was down. `game_welcome` is the app's
          // own definition of reconnected, and it fires on the first join and every re-join.
          setErrorReportingSlot(msg.slotId);
          void flushClientErrors();
          // Restore the desktop pilot pick remembered for this slot, so a returning player
          // re-lands on the pilot they last drove.
          setSelectedPilotId(readSelectedPilot(msg.slotId));
          setToken(msg.token); // the roster/encounter sockets present this in their hello
          setRole(msg.role as "gm" | "player");
          const view = msg.viewState as GameViewState;
          setViewState(view);
          // Seed the slot-presence list from the welcome's full state, marking at least
          // our own slot connected. The server sends a `game_change` right after the
          // welcome (the `bind` → `broadcastChange`), which fills in the rest of the
          // live `connected` set — this seed just avoids an empty-then-populated flash.
          setSlots(slotsFromState(msg.state, [msg.slotId]));
          setPlayerBanner(playerBannerFromState(msg.state));
          // The table scene rides the durable state: seed it from the welcome so a player
          // landing/reconnecting immediately knows whether a fight is live.
          setTableSceneState(tableSceneFromState(msg.state));
          // Whether a room password is set — derived from the same durable state, live from
          // the first welcome, so a GM reconnecting sees the current setting immediately.
          setPasswordSet(passwordSetFromState(msg.state));
          setSavedRulings(savedRulingsFromState(msg.state));
          setBackdropState(backdropFromState(msg.state));
          // Seed the chat & activity feed from the welcome's bounded tail so COMMS.app shows
          // recent history immediately on (re)join.
          setFeed(msg.feed);
          // Re-seed the freshness baseline from THIS backfill — every welcome, including a
          // mid-session reconnect, so a re-delivered tail never reads as "new" to a consumer like
          // `DiceFeedSubscriber`. Empty backfill -> 0 (a subsequent live append with ts > 0 is fresh).
          setFeedBaselineTs(msg.feed.reduce((max, e) => Math.max(max, e.ts), 0));
          // The server re-sends every decision card still open for this slot right after the
          // welcome, so start clean: a card resolved while the link was down must not stay pinned.
          setDecisions((prev) => nextDecisions(prev, msg));
          setStatus("authenticated");
          setReclaiming(false); // a reclaim (or any join) resolved — never hold the reconnecting state past authentication
          // Re-apply the slot's STANDING view-state through the effect registry: the
          // banner is re-derived from the persisted view-state, so a dropped player
          // gets it back, in its tone.
          applyStanding(view);
          // Remember the issued token for a frictionless reconnect. A failed write is
          // swallowed: storage may be unavailable (private mode) and the session still
          // works, it just won't survive a reload.
          storageSet(tokenKey(gameNameRef.current), msg.token);
          break;
        }
        case "game_reject":
          setRejectReason(msg.reason);
          setStatus("rejected");
          setReclaiming(false); // a reclaim attempt that bounced is settled — drop to the login form
          // The credential itself was refused, so retrying it would just be refused again:
          // stop reconnecting and let the login form take over.
          abandonedRef.current = true;
          welcomedRef.current = false;
          setLinkDown(false);
          setLinkStale(false);
          // A bad/stale token shouldn't strand the user — clear it so the dropdown shows.
          if (msg.reason === "bad_token") {
            tokenRef.current = null;
            storageRemove(tokenKey(gameNameRef.current));
          }
          break;
        case "chat_appended":
          // A new feed entry (chat / system / roll) was broadcast. Append to the bounded
          // in-memory tail, mirroring the server's cap.
          setFeed((prev) => {
            const next = [...prev, msg.entry];
            return next.length > FEED_TAIL_CAP ? next.slice(-FEED_TAIL_CAP) : next;
          });
          break;
        case "game_kicked":
          setStatus("kicked");
          setKickReason(msg.reason);
          // Eviction is another socket deliberately claiming this slot (a second tab, another
          // device). Reconnecting would evict it straight back, and if it also auto-reconnects
          // the two kick each other forever — so a kick is where the session stays down,
          // regardless of reason.
          abandonedRef.current = true;
          welcomedRef.current = false;
          setLinkDown(false);
          setLinkStale(false);
          setReclaiming(false);
          // "removed" means the slot itself is gone server-side and its token was already
          // revoked (`evictOrphanedConnections`) — keeping the stale token around would just
          // burn the next reload on a doomed reclaim round-trip that ends in a misleading
          // "link expired." "evicted" leaves the token alone: the slot still exists and this
          // device may legitimately reclaim it later (a second tab/device took over).
          if (msg.reason === "removed") {
            tokenRef.current = null;
            storageRemove(tokenKey(gameNameRef.current));
          }
          break;
        case "game_change":
          // The live "who's here" changed (a join/leave/reclaim, or a durable-state
          // edit). Re-derive the slot-presence list from the broadcast state + the live
          // `connected` set — this is the realtime feed the lobby's slot-presence pane reads.
          setSlots(slotsFromState(msg.state, msg.connected));
          setPlayerBanner(playerBannerFromState(msg.state));
          // Track the live table scene: the GM switching it broadcasts here, so the JOIN
          // affordance appears/vanishes and a player on a fight taken off the table gets bounced.
          setTableSceneState(tableSceneFromState(msg.state));
          // Track whether a room password is set, live — a GM setting/clearing it
          // rebroadcasts state, so every connected client's `passwordSet` reflects it immediately.
          setPasswordSet(passwordSetFromState(msg.state));
          setSavedRulings(savedRulingsFromState(msg.state));
          setBackdropState(backdropFromState(msg.state));
          break;
        case "game_view": {
          // A live standing-view-state push on the durable channel: the GM updated this
          // slot's view-state. Re-derive + apply the standing effects, and surface the
          // new view-state.
          const view = msg.viewState as GameViewState;
          setViewState(view);
          applyStanding(view);
          break;
        }
        case "game_fx_event": {
          // An ephemeral FX on the fire-and-forget channel: run the registry handler
          // once. NOT persisted, NOT replayed.
          const ctx = effectsRef.current;
          if (ctx !== undefined) runDirective({ registry: registryRef.current, directive: msg.directive, ctx, expectedKind: "ephemeral" });
          break;
        }
        case "game_decision":
        case "game_decision_resolved":
          setDecisions((prev) => nextDecisions(prev, msg));
          break;
      }
    };
  }, [applyStanding, detachSocket, scheduleReconnect]);
  connectRef.current = connect;

  /**
   * The in-flight `refreshSlotList()` round-trip, if any. A single tab-restore commonly
   * fires BOTH `window` `focus` and `document` `visibilitychange` for the same logical
   * event — both listeners in the effect below are kept (they cover genuinely different
   * app-switch vs. tab-switch cases), so without this a single restore would issue two
   * `GET /api/game/slots`.
   * Holding the **promise** (not a bare boolean) means every coalesced caller — including
   * the mount effect, which needs the parsed list back to make its token-reclaim decision —
   * awaits the one real fetch and gets the one real result, rather than a second caller
   * getting nothing back. Cleared once the fetch settles, so a later, genuinely separate
   * event still starts its own fresh round-trip (sequential calls are never coalesced).
   */
  const inFlightRefreshRef = useRef<Promise<SlotListResponse | null> | null>(null);

  /**
   * Fetch the pre-connect slot list (`GET /api/game/slots`) and store it. Shared by the
   * mount effect and the two staleness triggers below so all three read off one code path
   * rather than a hand-copy that can drift. Returns the parsed list on success so a caller
   * that needs it synchronously (the mount effect's token-reclaim decision) doesn't have to
   * re-read state; `null` on failure so callers can tell "fetched, empty-ish" apart from
   * "the fetch itself broke" without a second round of state reads. Concurrent callers
   * collapse onto the same in-flight round-trip — see {@link inFlightRefreshRef}.
   */
  const refreshSlotList = useCallback((): Promise<SlotListResponse | null> => {
    if (inFlightRefreshRef.current !== null) return inFlightRefreshRef.current;
    const promise = (async () => {
      try {
        const res = await fetch(GAME_SLOTS_PATH);
        const list = (await res.json()) as SlotListResponse;
        gameNameRef.current = list.gameName;
        setSlotList(list);
        return list;
      } catch {
        return null;
      } finally {
        inFlightRefreshRef.current = null;
      }
    })();
    inFlightRefreshRef.current = promise;
    return promise;
  }, []);

  /**
   * Re-present the token the moment the environment says a retry could work — the tab
   * coming back to the foreground, or the OS reporting the network is up again. This is
   * what rescues a laptop resumed after a long sleep: the backoff has usually exhausted
   * itself by then, and waiting for a timer that already gave up would leave the visitor
   * staring at a login screen they never asked for. Also the manual RELINK NOW
   * ({@link GameSession.reconnect}).
   */
  const reconnect = useCallback((): void => {
    if (abandonedRef.current) return;
    if (tokenRef.current === null) return;
    const ws = wsRef.current;
    // Already connected or mid-connect — nothing to revive.
    if (ws !== null && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;
    attemptRef.current = 0; // a fresh signal deserves a fresh budget, not the tail of the old backoff
    setLinkStale(false);
    connectRef.current?.(tokenRef.current);
  }, []);
  useEffect(() => {
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") reconnect();
    };
    window.addEventListener("online", reconnect);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("online", reconnect);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [reconnect]);

  /**
   * Refetch the pre-connect slot list on window focus / tab visibility, while
   * unauthenticated — the second of the two event-driven staleness triggers (the other is
   * the rejected-join refetch inside {@link loginThenConnect}). Covers the player parked on
   * the login screen when the GM sets a room password or defines a slot mid-setup: the
   * mount effect's one-shot fetch would otherwise never see it, and there is no socket yet
   * to ride a broadcast on. No polling, no timers — purely event-driven, and only while
   * there's a stale REST snapshot worth chasing: once authenticated, the live `/ws/game`
   * socket carries `game_change` / `passwordSet` updates directly, so refetching the
   * pre-connect REST list would be pointless work on every tab-switch.
   *
   * Distinct from the {@link reconnect} listener just above, even though both listen to
   * `visibilitychange`: that one re-presents a REMEMBERED TOKEN over the ws socket (and
   * bails immediately when there's no token to reclaim); this one re-fetches the pre-connect
   * REST snapshot (and bails once authenticated). Their guard conditions don't overlap in a
   * way that could make one trigger the other, so sharing the DOM event is safe — each
   * effect owns its own listener and its own no-op condition.
   */
  useEffect(() => {
    const maybeRefresh = (): void => {
      if (statusRef.current === "authenticated") return;
      void refreshSlotList();
    };
    const onFocusVisibility = (): void => {
      if (document.visibilityState === "visible") maybeRefresh();
    };
    window.addEventListener("focus", maybeRefresh);
    document.addEventListener("visibilitychange", onFocusVisibility);
    return () => {
      window.removeEventListener("focus", maybeRefresh);
      document.removeEventListener("visibilitychange", onFocusVisibility);
    };
  }, [refreshSlotList]);

  /**
   * REST-login with a credential (player slot+password or the GM host key), then open the
   * token-only ws hello with the minted token. A `401`/`409` is mapped to the reject UI via
   * `rejectReason`; an unreachable endpoint drops to `closed`.
   */
  const loginThenConnect = useCallback(async (credential: { slotId?: string; password?: string; hostKey?: string }) => {
    setRejectReason(null);
    setKickReason(null); // a deliberate fresh login attempt supersedes any prior kick line
    setStatus("connecting");
    attemptRef.current = 0; // a deliberate login starts the reconnect budget over
    const out = await restLogin(credential);
    if (!out.ok) {
      if (out.error.status === 0) {
        setStatus("closed"); // endpoint unreachable — not a credential rejection
      } else {
        setRejectReason(out.error.reason);
        setStatus("rejected");
        // A credential reject means the pre-connect snapshot this attempt was judged
        // against may be stale (the GM set a password, or removed the slot, since the page
        // loaded) — refetch so the login form reflects reality on the very next render
        // rather than waiting for a focus event that may never come before the player
        // tries again.
        void refreshSlotList();
      }
      setReclaiming(false);
      return;
    }
    connect(out.value.token);
  }, [connect, refreshSlotList]);

  // On mount: fetch the slot list, then auto-reclaim if a token is remembered.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await refreshSlotList();
      if (cancelled) return;
      if (list === null) {
        setStatus("closed");
        setReclaiming(false); // can't even fetch the slot list — not a reclaim, surface the (login) UI
        return;
      }
      // An unreachable store reads as "nothing remembered" — the visitor sees the login.
      const storedToken = storageGet(tokenKey(list.gameName));
      const token: string | null = storedToken.ok ? (storedToken.value ?? null) : null;
      // Restore a remembered GM host key so GM-gated REST works after a reload (the
      // token reclaim re-asserts the GM role; this re-arms the host-key credential).
      const savedKey = storageGet(hostKeyKey(list.gameName));
      if (savedKey.ok && savedKey.value) setHostKey(savedKey.value);
      if (token) {
        connect(token); // reclaim in flight — keep `reclaiming` true until welcome/reject settles it
      } else {
        setReclaiming(false); // nothing remembered — the visitor is logged out, show the login
      }
    })();
    return () => {
      cancelled = true;
      // Unmounting is a deliberate teardown, not a drop — abandon before detaching so the
      // socket's close can't schedule a reconnect against a dead component.
      abandonedRef.current = true;
      if (retryTimerRef.current !== null) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      genRef.current += 1;
      detachSocket();
    };
  }, [connect, detachSocket, refreshSlotList]);

  const selectPilot = useCallback((pilotId: string) => {
    if (actorId === null) return; // no authenticated slot to key the persistence on
    setSelectedPilotId(pilotId);
    writeSelectedPilot(actorId, pilotId);
  }, [actorId]);
  const deselectPilot = useCallback(() => {
    if (actorId === null) return; // no authenticated slot to key the persistence on
    setSelectedPilotId(null);
    clearSelectedPilot(actorId);
  }, [actorId]);
  const joinSlot = useCallback((slotId: string, password?: string) => {
    void loginThenConnect({ slotId, password });
  }, [loginThenConnect]);
  const joinAsGm = useCallback((key: string) => {
    // Remember the host key for GM-gated REST (scene save) across the session + a reconnect.
    setHostKey(key);
    // A failed write is swallowed — the in-session hostKey state still works.
    storageSet(hostKeyKey(gameNameRef.current), key);
    void loginThenConnect({ hostKey: key });
  }, [loginThenConnect]);
  const setTableScene = useCallback((scene: TableScene): boolean => {
    const ws = wsRef.current;
    if (ws === null || ws.readyState !== WebSocket.OPEN || statusRef.current !== "authenticated" || actorId === null) return false;
    // The room re-attributes to the authenticated slot and gates the channel GM-only, so `actorId`
    // here is the honest GM slot, never a client claim (the wire `actorId` is server-verified
    // identity, not a chosen id — CLAUDE.md). A non-GM send is a server no-op; we don't pre-check
    // the role here (the gate is the wall; the UI just hides it).
    const command: SetTableSceneCommand = { type: "set_table_scene", actorId, scene };
    ws.send(encode({ type: "game_command", actorId, command }));
    return true;
  }, [actorId]);
  const setBackdrop = useCallback((entryId: string | null): boolean => {
    const ws = wsRef.current;
    if (ws === null || ws.readyState !== WebSocket.OPEN || statusRef.current !== "authenticated" || actorId === null) return false;
    const command: SetBackdropCommand = { type: "set_backdrop", actorId, entryId };
    ws.send(encode({ type: "game_command", actorId, command }));
    return true;
  }, [actorId]);
  const defineSlot = useCallback((name: string, role: SlotRole = "player"): string | null => {
    const ws = wsRef.current;
    if (ws === null || ws.readyState !== WebSocket.OPEN || statusRef.current !== "authenticated" || actorId === null) return null;
    // Mint against the LIVE broadcast-driven slots list (never a stale local copy) — the
    // server gives no error reply on a collision, so avoiding it here IS the only guard.
    const slotId = mintSlotId(name, slots.map((s) => s.id));
    ws.send(encode(defineSlotMessage({ actorId, slotId, name, role })));
    return slotId;
  }, [actorId, slots]);
  const setSlotRole = useCallback((slotId: string, role: SlotRole): boolean => {
    const ws = wsRef.current;
    if (ws === null || ws.readyState !== WebSocket.OPEN || statusRef.current !== "authenticated" || actorId === null) return false;
    // Identity is server-bound exactly like `defineSlot`/`removeSlot`/`setTableScene` —
    // no local role pre-check, the server gate (isGm) is the wall. The reducer's last-GM
    // demotion refusal is a UI-side affordance decision (disable the control via
    // `isOnlyGmSlot`), not something pre-checked here — see the doc comment on
    // `GameSession.setSlotRole`.
    ws.send(encode(setSlotRoleMessage({ actorId, slotId, role })));
    return true;
  }, [actorId]);
  const removeSlot = useCallback((slotId: string): boolean => {
    const ws = wsRef.current;
    if (ws === null || ws.readyState !== WebSocket.OPEN || statusRef.current !== "authenticated" || actorId === null) return false;
    // Identity is server-bound exactly like `defineSlot`/`setTableScene` — no local
    // role pre-check, the server gate (isGm) is the wall. The reducer's last-GM-slot
    // refusal is a UI-side affordance decision (disable the control), not something
    // pre-checked here — see the doc comment on `GameSession.removeSlot`.
    ws.send(encode(removeSlotMessage({ actorId, slotId })));
    return true;
  }, [actorId]);
  const saveRuling = useCallback((savedRuling: SavedRuling): boolean => {
    const ws = wsRef.current;
    if (ws === null || ws.readyState !== WebSocket.OPEN || statusRef.current !== "authenticated" || actorId === null) return false;
    // Identity is server-bound exactly like `defineSlot`/`removeSlot` — no local role
    // pre-check, the server gate (isGm) is the wall. Unlike `defineSlot`, there is no
    // collision to mint around: a save onto an existing id is the deliberate OVERWRITE
    // gesture, not a reducer rejection to dodge.
    ws.send(encode(saveRulingMessage({ actorId, savedRuling })));
    return true;
  }, [actorId]);
  const deleteSavedRuling = useCallback((id: string): boolean => {
    const ws = wsRef.current;
    if (ws === null || ws.readyState !== WebSocket.OPEN || statusRef.current !== "authenticated" || actorId === null) return false;
    ws.send(encode(deleteSavedRulingMessage({ actorId, id })));
    return true;
  }, [actorId]);
  const setRoomPassword = useCallback((password: string | null): boolean => {
    const ws = wsRef.current;
    if (ws === null || ws.readyState !== WebSocket.OPEN || statusRef.current !== "authenticated" || actorId === null) return false;
    // A DIFFERENT wire path from `defineSlot`/`removeSlot`/`setTableScene` — this is
    // `game_set_password` directly, never a `game_command` envelope (see the doc comment
    // on `GameSession.setRoomPassword`). The plaintext crosses the wire; the server hashes
    // it (argon2id) — never hash here. No local role pre-check, same as the other three.
    ws.send(encode(setPasswordMessage(password)));
    return true;
  }, [actorId]);
  const sendFx = useCallback((directive: Directive, target: DirectiveTarget = "all"): boolean => {
    const ws = wsRef.current;
    // GM gate at the UI (affordance) — the server's `GameRoom.handle` gate is the real wall, but
    // gating here avoids sending a directive that will only be silently dropped. Also require an
    // open, authenticated socket.
    if (ws === null || ws.readyState !== WebSocket.OPEN || statusRef.current !== "authenticated" || role !== "gm") return false;
    // The EPHEMERAL channel: a generic `game_fx` envelope, NO new message type per effect. Routed
    // to live sockets as `game_fx_event` and never persisted — distinct from `setView`'s durable path.
    ws.send(encode(fxMessage(directive, target)));
    return true;
  }, [role]);
  const postChat = useCallback((text: string): boolean => {
    const ws = wsRef.current;
    if (ws === null || ws.readyState !== WebSocket.OPEN || statusRef.current !== "authenticated" || actorId === null) return false;
    if (text.trim() === "") return false; // don't send an empty post (the server drops it anyway)
    // The author is server-stamped — `chat_post` carries no author. The server builds the
    // entry and broadcasts `chat_appended`.
    ws.send(encode({ type: "chat_post", text }));
    return true;
  }, [actorId]);
  const setView = useCallback((patch: ViewStatePatch, target: DirectiveTarget = "all"): boolean => {
    const ws = wsRef.current;
    if (ws === null || ws.readyState !== WebSocket.OPEN || statusRef.current !== "authenticated" || role !== "gm") return false;
    // The DURABLE channel: a `game_set_view` patch the server persists (via `set_slot_view`) and
    // replays on reconnect. We do NOT set the local `viewState` optimistically — the server is
    // authoritative and pushes `game_view` back (the GM only sees the change locally if it targeted
    // its own slot, which is correct: the held view reflects what the SERVER stored).
    ws.send(encode(setViewMessage(patch, target)));
    return true;
  }, [role]);
  const forget = useCallback(() => {
    // A logout must not be undone by the reconnect machinery: abandon, cancel any pending
    // retry, drop the token, and retire the socket before clearing the rest of the session.
    abandonedRef.current = true;
    welcomedRef.current = false;
    tokenRef.current = null;
    attemptRef.current = 0;
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    genRef.current += 1;
    detachSocket();
    storageRemove(tokenKey(gameNameRef.current));
    storageRemove(hostKeyKey(gameNameRef.current));
    // Drop the remembered desktop pilot pick for this slot: logging out is a clean break,
    // not a switch — the next login re-picks from scratch.
    setActorId((prev) => {
      if (prev !== null) clearSelectedPilot(prev);
      return null;
    });
    setSelectedPilotId(null);
    setToken(null);
    setRole(null);
    setHostKey(null);
    setViewState(null);
    setSlots([]);
    setPlayerBanner(null);
    setTableSceneState(NARRATIVE_SCENE);
    setBackdropState(null);
    setPasswordSet(false);
    setSavedRulings([]);
    setKickReason(null); // an explicit logout supersedes any prior kick line too
    setFeed([]);
    setFeedBaselineTs(0);
    setDecisions({});
    setLinkDown(false);
    setLinkStale(false);
    setStatus("idle");
    setReclaiming(false); // an explicit logout is not a reclaim — go straight to the login form
  }, [detachSocket]);

  const dismissDecision = useCallback((promptId: string) => {
    setDecisions((prev) => withoutDecision(prev, promptId));
  }, []);

  return { status, reclaiming, linkDown, linkStale, reconnect, slotList, actorId, selectedPilotId, token, role, hostKey, viewState, slots, playerBanner, tableScene, activeEncounterId, backdrop, passwordSet, savedRulings, rejectReason, kickReason, feed, feedBaselineTs, decisions, dismissDecision, postChat, selectPilot, deselectPilot, joinSlot, joinAsGm, setTableScene, setBackdrop, defineSlot, setSlotRole, removeSlot, setRoomPassword, saveRuling, deleteSavedRuling, sendFx, setView, forget };
}
